import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SubscriptionStatusBadge } from './subscription-status-badge';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, CreditCard, ArrowRight, Sparkles, ArrowUpRight } from 'lucide-react';
import { format } from 'date-fns';
import type { Subscription } from '@/services/subscription/subscription.service';
import { formatDaysRemaining, getDaysRemaining } from '@/utils/format-subscription';

interface CurrentSubscriptionCardProps {
  subscription: Subscription;
  onTopUp?: () => void;
  onUpgradePlan?: () => void;
}

export function CurrentSubscriptionCard({ subscription, onTopUp, onUpgradePlan }: CurrentSubscriptionCardProps) {
  const { t } = useTranslation();

  const startDate = new Date(subscription.currentPeriodStart);
  const endDate = new Date(subscription.currentPeriodEnd);
  
  // Use the utility function for formatting
  const daysRemainingText = formatDaysRemaining(endDate);
  const totalDays = getDaysRemaining(endDate);

  const isLowDays = totalDays < 30 && totalDays >= 0;
  const isVeryLowDays = totalDays < 7 && totalDays >= 0;

  return (
    <Card className="border-slate-200 bg-gradient-to-br from-white to-slate-50/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-slate-900">{t('billing.currentSubscription.title')}</CardTitle>
            <CardDescription className="text-slate-600 mt-1">
              {t('billing.currentSubscription.description')}
            </CardDescription>
          </div>
          <SubscriptionStatusBadge status={subscription.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Days Remaining - Aligned like IMS client */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-slate-500" />
            <p className="text-sm text-slate-600">{t('billing.currentSubscription.daysRemaining')}</p>
          </div>
          <div className="flex items-baseline gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
            <span
              className={`text-xl sm:text-2xl font-bold tracking-tight ${
                isVeryLowDays
                  ? 'text-red-600'
                  : isLowDays
                  ? 'text-amber-600'
                  : 'text-emerald-600'
              }`}
            >
              {daysRemainingText}
            </span>
            {totalDays > 30 && (
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500 animate-pulse" />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mt-4 sm:mt-5 md:mt-6">
          {/* Start Date */}
          <div>
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Calendar className="h-4 w-4 text-slate-500" />
              <p className="text-sm text-slate-600">{t('billing.currentSubscription.startDate')}</p>
            </div>
            <p className="text-base sm:text-lg font-semibold text-slate-800 mt-0.5 sm:mt-1">
              {format(startDate, 'MMM dd, yyyy')}
            </p>
          </div>

          {/* End Date */}
          <div>
            <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
              <Calendar className="h-4 w-4 text-slate-500" />
              <p className="text-sm text-slate-600">{t('billing.currentSubscription.endDate')}</p>
            </div>
            <p className="text-base sm:text-lg font-semibold text-slate-800 mt-0.5 sm:mt-1">
              {format(endDate, 'MMM dd, yyyy')}
            </p>
          </div>
        </div>

        {/* Plan Info */}
        <div className="pt-4 border-t">
          <p className="text-sm text-slate-600 mb-1">{t('billing.currentSubscription.plan')}</p>
          <p className="text-lg font-semibold text-slate-800">{subscription.plan.displayName}</p>
          <p className="text-sm text-slate-500 mt-1">
            ${subscription.plan.priceInCents} / {t('billing.month')}
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={onTopUp}
          variant="default"
          className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/90 text-white hover:from-primary/90 hover:to-primary/80"
        >
          <CreditCard className="h-4 w-4 mr-2" />
          {t('billing.currentSubscription.topUpButton')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button
          onClick={onUpgradePlan}
          variant="outline"
          className="w-full sm:w-auto"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {t('billing.currentSubscription.upgradePlanButton')}
          <ArrowUpRight className="h-4 w-4 ml-2" />
        </Button>
      </CardFooter>
    </Card>
  );
}

